import React from "react";
import type { OrganizationPageService } from "../../presentation/organizations/organizationPage.types";
import { buildWhatsAppUrl } from "../../domain/shared/commerce.utils";
import ServiceCard from "./ServiceCard";

interface ServicesSectionProps {
  services: OrganizationPageService[];
  fallbackImage: string;
  title: string;
  emptyTitle: string;
  emptyDescription?: string;
}

const getActionHref = (service: OrganizationPageService): string | undefined => {
  const action = service.primaryAction;
  if (!action) return undefined;
  if (action.method === "whatsapp") return buildWhatsAppUrl(action.target, action.messageTemplate);
  if (action.method === "email") return `mailto:${action.target}`;
  return action.target;
};

const ServicesSection: React.FC<ServicesSectionProps> = ({
  services,
  fallbackImage,
  title,
  emptyTitle,
  emptyDescription,
}) => {
  const orderedServices = [...services].sort((left, right) => left.order - right.order);
  const hasSingleService = orderedServices.length === 1;

  return (
    <section className="bg-gray-50 py-16" data-testid="services-section">
      <div className="container mx-auto px-4">
        <h2 className="mb-10 text-center text-3xl font-bold text-gray-800">{title}</h2>
        {orderedServices.length ? (
          <div className={hasSingleService ? "grid grid-cols-1 gap-8" : "grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3"}>
            {orderedServices.map((service) => (
              <ServiceCard
                key={service.id}
                service={service}
                fallbackImage={fallbackImage}
                actionHref={getActionHref(service)}
                wide={hasSingleService}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-gray-200 bg-white py-12 text-center shadow-lg">
            <h3 className="text-xl font-semibold text-gray-700">{emptyTitle}</h3>
            {emptyDescription && <p className="mt-2 text-gray-500">{emptyDescription}</p>}
          </div>
        )}
      </div>
    </section>
  );
};

export default ServicesSection;
